import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';

import useSafeFormikContext from '~/components/hooks/useSafeFormikContext';

import { useClaim } from '../ClaimContainer';

import { useActivePolicyCategories } from './useActivePolicyCategories';
import { useIncidentConfiguration } from './useIncidentConfiguration';

const TYPES_CONFIG_PATH = 'incident_details.types';
const DEFAULT_TYPE = 'other';

// Lets a parent (e.g. AdditionalInformationSection) override the incident
// type/sub-type lookup across an isolated <Formik> boundary, where the hook's
// formik fallback would otherwise read the inner dialog formik (no incident_type).
const IncidentTypeOverrideContext = createContext(undefined);

const IncidentTypeOverrideProvider = ({ value, children }) => (
  <IncidentTypeOverrideContext.Provider value={value}>{children}</IncidentTypeOverrideContext.Provider>
);

IncidentTypeOverrideProvider.propTypes = {
  value: PropTypes.shape({
    incidentType: PropTypes.string,
    incidentSubType: PropTypes.string,
  }),
  children: PropTypes.node,
};

const useIsConfigurationFieldSupportedByCategories = () => {
  const formikProps = useSafeFormikContext();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { claim } = useClaim();
  const incidentTypeOverride = useContext(IncidentTypeOverrideContext);
  // Dual-source active policy categories: the FNOL wizard mounts a provider (fetched per
  // policy change); claim surfaces have no provider (null) and read the list the claim
  // payload embeds. Either way the FE only does set-membership — the backend evaluated.
  const wizardActivePolicyCategories = useActivePolicyCategories();
  const activePolicyCategories = wizardActivePolicyCategories ?? claim?.active_policy_category_ids ?? [];

  const getCategoryMatchingLogic = (supportedTypes, incidentType, incidentSubType) => {
    // if the field/option is not supported by any subtype, it is supported by all
    if (isEmpty(supportedTypes)) {
      return true;
    }

    // Every limit-list entry is an independent reason to show the field: an active policy
    // category matches regardless of the selected incident type/sub-type (OR semantics).
    if (activePolicyCategories.some((categoryId) => supportedTypes.includes(categoryId))) {
      return true;
    }

    // If incidentType or incidentSubType is not set, only show fields/options that support "other"
    // (fields/options with no categories are already handled above)
    if (!incidentType || !incidentSubType) {
      return supportedTypes.includes(DEFAULT_TYPE);
    }

    const incidentTypeConfig = get(incidentConfiguration, TYPES_CONFIG_PATH) || [];
    const incidentTypeConfigItem = incidentTypeConfig?.find((item) => item.id === incidentType);
    const incidentSubTypes = incidentTypeConfigItem?.sub_types || [];
    const supportedCategory = incidentSubTypes?.find((item) => item.id === incidentSubType)?.category;

    // if field supports other and current incident_sub_type is not set or without type attribute, it's supported
    if (!supportedCategory && supportedTypes.includes(DEFAULT_TYPE)) {
      return true;
    }

    return supportedTypes.includes(supportedCategory);
  };

  const resolveIncidentType = (selectedType) => {
    if (selectedType) return selectedType;
    if (!isNil(incidentTypeOverride)) return incidentTypeOverride.incidentType;
    return formikProps?.values?.incident_type || claim?.incident?.incident_type;
  };

  const resolveIncidentSubType = (selectedSubType) => {
    if (selectedSubType) return selectedSubType;
    if (!isNil(incidentTypeOverride)) return incidentTypeOverride.incidentSubType;
    return formikProps?.values?.incident_sub_type || claim?.incident?.incident_sub_type;
  };

  const isFieldSupportedByCategories = (field, selectedType, selectedSubType) => {
    const supportedTypes = field?.incident_sub_type_categories || [];
    const incidentType = resolveIncidentType(selectedType);
    const incidentSubType = resolveIncidentSubType(selectedSubType);

    return getCategoryMatchingLogic(supportedTypes, incidentType, incidentSubType);
  };

  const isOptionSupportedByCategories = (field, option, selectedType, selectedSubType) => {
    // First check field-level categories (field-level "wins")
    const fieldSupported = isFieldSupportedByCategories(field, selectedType, selectedSubType);
    if (!fieldSupported) {
      return false;
    }

    // If field-level check passes (or field has no categories), check option-level categories
    const optionSupportedTypes = option?.incident_sub_type_categories || [];
    const incidentType = resolveIncidentType(selectedType);
    const incidentSubType = resolveIncidentSubType(selectedSubType);

    // If option has no categories, show it (field-level already passed)
    if (isEmpty(optionSupportedTypes)) {
      return true;
    }

    // If option has categories, check if they match current sub-type
    return getCategoryMatchingLogic(optionSupportedTypes, incidentType, incidentSubType);
  };

  return {
    isFieldSupportedByCategories,
    isOptionSupportedByCategories,
  };
};

export { IncidentTypeOverrideProvider };
export default useIsConfigurationFieldSupportedByCategories;
